import { z } from "zod";
import {
  REBOQUE_REQUIRED_FIELDS,
  hasValue,
  isReboqueComplete,
  isReboqueStarted,
  isVehicleTypeRequiringReboque,
} from "./constants";
import {
  VEHICLE_VALIDATION_MESSAGES,
  getReboqueFieldRequiredMessage,
} from "./messages";

const optionalString = z.string().optional().nullable();

const reboqueSchema = z
  .object({
    id: optionalString,
    placaReboque: optionalString,
    renavamReboque: optionalString,
    ufReboque: optionalString,
    descricaoReboque: optionalString,
    rntrcReboque: optionalString,
    tipoRNTRCReboque: optionalString,
    TipoCarroceriaReboque: optionalString,
    anttReboque: optionalString,
    cnhReboque: optionalString,
    capacidadeKGReboque: optionalString,
    capacidadeM3Reboque: optionalString,
    pesoTaraReboque: optionalString,
    tipoProprietarioReboque: optionalString,
    cpfoucnpjReboque: optionalString,
    nomeProprietarioReboque: optionalString,
  })
  .passthrough();

const addRequiredIssue = (ctx, path, message) => {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path,
    message,
  });
};

const addMissingReboqueFieldIssues = (ctx, reboque, index) => {
  REBOQUE_REQUIRED_FIELDS.forEach((field) => {
    if (!hasValue(reboque[field.key])) {
      addRequiredIssue(
        ctx,
        ["reboques", index, field.key],
        getReboqueFieldRequiredMessage(index, field.key),
      );
    }
  });
};

/**
 * Schema Zod do formulário de veículo trator.
 * Reboques vinculados por trailerIds (docs trailers:true) + reboques[] legado.
 */
export const vehicleFormSchema = z
  .object({
    vehiclePlate: optionalString,
    renavam: optionalString,
    ufVeiculo: optionalString,
    documentCRLV: optionalString,
    descricaoVeiculo: optionalString,
    vehicleType: optionalString,
    rntrc: optionalString,
    tipoRNTRC: optionalString,
    bodyworkType: optionalString,
    bodyworkPlate: optionalString,
    documentAnttFrontImg: optionalString,
    pesoTara: optionalString,
    capacidadeKG: optionalString,
    capacidadeM3: optionalString,
    tipoProprietario: optionalString,
    cpfoucnpj: optionalString,
    nomeProprietario: optionalString,
    vehicleImg: optionalString,
    trailerIds: z.array(z.string()).default([]),
    reboques: z.array(reboqueSchema).default([]),
  })
  .superRefine((data, ctx) => {
    if (!hasValue(data.vehiclePlate)) {
      addRequiredIssue(ctx, ["vehiclePlate"], VEHICLE_VALIDATION_MESSAGES.vehiclePlate);
    }
    if (!hasValue(data.renavam)) {
      addRequiredIssue(ctx, ["renavam"], VEHICLE_VALIDATION_MESSAGES.renavam);
    }
    if (!hasValue(data.ufVeiculo)) {
      addRequiredIssue(ctx, ["ufVeiculo"], VEHICLE_VALIDATION_MESSAGES.ufVeiculo);
    }
    if (!hasValue(data.vehicleType)) {
      addRequiredIssue(ctx, ["vehicleType"], VEHICLE_VALIDATION_MESSAGES.vehicleType);
    }
    if (!hasValue(data.rntrc)) {
      addRequiredIssue(ctx, ["rntrc"], VEHICLE_VALIDATION_MESSAGES.rntrc);
    }
    if (!hasValue(data.tipoRNTRC)) {
      addRequiredIssue(ctx, ["tipoRNTRC"], VEHICLE_VALIDATION_MESSAGES.tipoRNTRC);
    }

    const requiresReboque = isVehicleTypeRequiringReboque(data.vehicleType);
    const reboques = Array.isArray(data.reboques) ? data.reboques : [];
    const trailerIds = Array.isArray(data.trailerIds)
      ? data.trailerIds.filter((id) => String(id || "").trim())
      : [];

    if (!requiresReboque && !hasValue(data.bodyworkType)) {
      addRequiredIssue(
        ctx,
        ["bodyworkType"],
        VEHICLE_VALIDATION_MESSAGES.bodyworkType,
      );
    }

    if (!hasValue(data.documentCRLV)) {
      addRequiredIssue(
        ctx,
        ["documentCRLV"],
        VEHICLE_VALIDATION_MESSAGES.documentCRLV,
      );
    }
    if (!hasValue(data.documentAnttFrontImg)) {
      addRequiredIssue(
        ctx,
        ["documentAnttFrontImg"],
        VEHICLE_VALIDATION_MESSAGES.documentAnttFrontImg,
      );
    }

    const completeEmbedded = reboques.filter(isReboqueComplete);
    const hasLinkedTrailer = trailerIds.length > 0;

    if (requiresReboque) {
      if (!hasLinkedTrailer && completeEmbedded.length === 0) {
        if (reboques.length === 0) {
          addRequiredIssue(
            ctx,
            ["trailerIds"],
            VEHICLE_VALIDATION_MESSAGES.reboqueRequired,
          );
        } else {
          reboques.forEach((reboque, index) => {
            if (isReboqueStarted(reboque)) {
              addMissingReboqueFieldIssues(ctx, reboque, index);
            }
          });
          if (!reboques.some(isReboqueStarted)) {
            addRequiredIssue(
              ctx,
              ["trailerIds"],
              VEHICLE_VALIDATION_MESSAGES.reboqueRequired,
            );
          }
        }
      } else {
        reboques.forEach((reboque, index) => {
          if (isReboqueStarted(reboque) && !isReboqueComplete(reboque)) {
            addMissingReboqueFieldIssues(ctx, reboque, index);
          }
        });
      }
    } else {
      reboques.forEach((reboque, index) => {
        if (isReboqueStarted(reboque)) {
          addMissingReboqueFieldIssues(ctx, reboque, index);
        }
      });
    }
  });
